import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";
import { Img } from "./Img";

export function QtyControl({ item, testPrefix }) {
  const { setQty } = useCart();
  return (
    <div className="flex items-center border border-[color:var(--line-2)]">
      <button onClick={() => setQty(item.key, item.qty - 1)} className="p-2 text-slate-400 hover:text-white" aria-label="Decrease" data-testid={`${testPrefix}-dec-${item.slug}`}><Minus className="h-3 w-3" /></button>
      <span className="w-8 text-center font-mono text-sm" data-testid={`${testPrefix}-qty-${item.slug}`}>{item.qty}</span>
      <button onClick={() => setQty(item.key, item.qty + 1)} className="p-2 text-slate-400 hover:text-white" aria-label="Increase" data-testid={`${testPrefix}-inc-${item.slug}`}><Plus className="h-3 w-3" /></button>
    </div>
  );
}

export function CartDrawer() {
  const { items, open, setOpen, remove, subtotal } = useCart();
  const nav = useNavigate();
  const go = (to) => { setOpen(false); nav(to); };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col border-l border-[color:var(--line)] bg-[#0d1014] p-0 sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader className="border-b border-[color:var(--line)] p-6">
          <SheetTitle className="font-display text-2xl uppercase tracking-wide">Basket</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6">
          {items.length === 0 ? (
            <div className="py-16 text-center">
              <p className="mb-6 text-slate-400">Your basket is empty.</p>
              <button className="btn-ghost" onClick={() => go("/shop")} data-testid="cart-drawer-browse">Browse the shop</button>
            </div>
          ) : (
            <ul className="space-y-5">
              {items.map((i) => (
                <li key={i.key} className="flex gap-4" data-testid={`cart-drawer-item-${i.slug}`}>
                  <div className="h-20 w-20 shrink-0 overflow-hidden bg-[color:var(--bg-3)]">
                    {i.image && <Img src={i.image} alt={i.name} sizes="80px" className="h-full w-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <Link to={`/product-page/${i.slug}`} onClick={() => setOpen(false)} className="text-sm font-medium leading-snug hover:text-[color:var(--ice)]">{i.name}</Link>
                    {Object.keys(i.options || {}).length > 0 && <p className="text-xs text-slate-500">{Object.entries(i.options).map(([a, b]) => `${a}: ${b}`).join(" · ")}</p>}
                    <p className="mt-1 font-mono text-sm text-[color:var(--ice)]">{money(i.price)}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <QtyControl item={i} testPrefix="cart-drawer" />
                      <button onClick={() => remove(i.key)} className="p-2 text-slate-500 hover:text-red-400" aria-label="Remove" data-testid={`cart-drawer-remove-${i.slug}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {items.length > 0 && (
          <div className="space-y-4 border-t border-[color:var(--line)] p-6">
            <div className="flex justify-between font-mono text-sm"><span className="text-slate-400">Subtotal</span><span data-testid="cart-drawer-subtotal">{money(subtotal)}</span></div>
            <p className="text-xs text-slate-500">Free UK delivery. Final total confirmed at checkout.</p>
            <div className="grid grid-cols-2 gap-3">
              <button className="btn-ghost !px-3" onClick={() => go("/cart")} data-testid="cart-drawer-view-cart">View basket</button>
              <button className="btn-ice !px-3" onClick={() => go("/checkout")} data-testid="cart-drawer-checkout">Checkout</button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
